import { useState, useEffect } from "react";
import toast from "react-hot-toast";

import LocationSelector from "./LocationSector";
import {
  saveDeliveryLocation,
  updateDeliveryLocation,
} from "../../utils/zulalWebAPI";

const AddressForm = ({ mode, address, onClose }) => {
  const [formData, setFormData] = useState({
    locationLabel: "",
    recipientName: "",
    phoneNumber: "",
    buildingName: "",
    flatNumber: "",
    landmark: "",
    country: "",
    state: "",
    city: "",
    area: "",
    isDefault: false,
  });

  const [locationValues, setLocationValues] = useState({});

  useEffect(() => {
    if (mode === "edit" && address) {
      setFormData(address);

      // Prepare location values for LocationSelector initial values
      setLocationValues({
        country: {
          countryId: address.countryId,
          countryName: address.countryName,
        },
        state: {
          stateId: address.stateId,
          stateCode: address.stateCode,
          statessName: address.statessName,
        },
        city: { cityId: address.cityId, cityName: address.cityName },
        area: { areaId: address.areaId, areaName: address.areaName },
      });
    }
  }, [mode, address]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleLocationChange = (location) => {
    setLocationValues(location);

    setFormData((prev) => ({
      ...prev,
      country: location.country,
      state: location.state,
      city: location.city,
      area: location.area,
    }));
  };

  const handleSubmit = async () => {
    // v2: plain JSON payload (no encryption, no secret key)
    const payload = {
      ...formData,
    };

    delete payload.customerCode;

    try {
      const response =
        mode === "edit"
          ? await updateDeliveryLocation(payload)
          : await saveDeliveryLocation(payload);

      if (response?.status === "success") {
        toast.success(
          mode === "edit"
            ? "Address updated successfully"
            : "Address saved successfully"
        );
        onClose();
        return;
      }

      // Keep the form open and restore location objects (same behavior as before)
      setFormData((prev) => ({
        ...prev,
        country: locationValues.country,
        state: locationValues.state,
        city: locationValues.city,
        area: locationValues.area,
      }));

      toast.error(
        mode === "edit" ? "Error updating address" : "Error saving address"
      );
    } catch (error) {
      console.error("Error saving/updating address:", error);

      // Keep the form open and restore location objects
      setFormData((prev) => ({
        ...prev,
        country: locationValues.country,
        state: locationValues.state,
        city: locationValues.city,
        area: locationValues.area,
      }));

      toast.error("Something went wrong while saving the address");
    }
  };

  return (
    <div>
      <div className="mx-auto mr-4 ml-4 mb-28">
        <div className="bg-white border border-cool-gray-400 p-4 rounded-xl shadow-md space-y-3">
          <div className="text-navarbarText text-3xl font-poppins font-medium mb-8">
            <h2 className="text-lg font-semibold">
              {mode === "edit" ? "Edit Address" : "Add New Address"}
            </h2>
            <p className="text-base text-darkTextColor font-poppins font-normal">
              Please update your current Location to serve you better
            </p>
          </div>

          <div>
            <label>Select Your area </label>
            <LocationSelector
              onLocationChange={handleLocationChange}
              initialLocationValues={locationValues}
            />
          </div>

          <div className="grid grid-cols-1 gap-x-8 gap-y-6 sm:grid-cols-2">
            {/* Location Label */}
            <div>
              <label
                htmlFor="locationLabel"
                className="block text-sm font-semibold leading-6 text-gray-900"
              >
                Location Label
              </label>
              <div className="mt-2.5">
                <input
                  type="text"
                  name="locationLabel"
                  placeholder="e.g. Home, Office"
                  value={formData.locationLabel}
                  onChange={handleChange}
                  id="locationLabel"
                  autoComplete="locationLabel"
                  className="block w-full rounded-md border-0 px-3.5 py-2 text-gray-900 shadow-sm ring-1 ring-inset ring-secondaryHover
                           placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-primary sm:text-sm sm:leading-6"
                />
              </div>
            </div>

            {/* Recipient Name */}
            <div>
              <label
                htmlFor="recipientName"
                className="block text-sm font-semibold leading-6 text-gray-900"
              >
                Recipient Name
              </label>
              <div className="mt-2.5">
                <input
                  type="text"
                  name="recipientName"
                  placeholder="e.g. Mohammed Ali"
                  value={formData.recipientName}
                  onChange={handleChange}
                  id="recipientName"
                  autoComplete="recipientName"
                  className="block w-full rounded-md border-0 px-3.5 py-2 text-gray-900 shadow-sm ring-1 ring-inset ring-secondaryHover
                  placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-primary sm:text-sm sm:leading-6"
                />
              </div>
            </div>

            {/* Phone Number */}
            <div>
              <label
                htmlFor="phoneNumber"
                className="block text-sm font-semibold leading-6 text-gray-900"
              >
                Phone Number
              </label>
              <div className="mt-2.5">
                <input
                  type="text"
                  name="phoneNumber"
                  placeholder="e.g. 1234567890"
                  value={formData.phoneNumber}
                  onChange={handleChange}
                  id="phoneNumber"
                  autoComplete="phoneNumber"
                  className="block w-full rounded-md border-0 px-3.5 py-2 text-gray-900 shadow-sm ring-1 ring-inset ring-secondaryHover
     placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-primary sm:text-sm sm:leading-6"
                />
              </div>
            </div>

            {/* Building Name */}
            <div>
              <label
                htmlFor="buildingName"
                className="block text-sm font-semibold leading-6 text-gray-900"
              >
                Building Name
              </label>
              <div className="mt-2.5">
                <input
                  type="text"
                  name="buildingName"
                  placeholder="e.g. Green Valley"
                  value={formData.buildingName}
                  onChange={handleChange}
                  id="buildingName"
                  autoComplete="buildingName"
                  className="block w-full rounded-md border-0 px-3.5 py-2 text-gray-900 shadow-sm ring-1 ring-inset ring-secondaryHover
     placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-primary sm:text-sm sm:leading-6"
                />
              </div>
            </div>

            {/* Flat number */}
            <div>
              <label
                htmlFor="flatNumber"
                className="block text-sm font-semibold leading-6 text-gray-900"
              >
                Flat / Villa Number
              </label>
              <div className="mt-2.5">
                <input
                  type="text"
                  name="flatNumber"
                  placeholder="e.g. 101"
                  value={formData.flatNumber}
                  onChange={handleChange}
                  id="flatNumber"
                  autoComplete="flatNumber"
                  className="block w-full rounded-md border-0 px-3.5 py-2 text-gray-900 shadow-sm ring-1 ring-inset ring-secondaryHover
         placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-primary sm:text-sm sm:leading-6"
                />
              </div>
            </div>

            {/* Landmark */}
            <div>
              <label
                htmlFor="landmark"
                className="block text-sm font-semibold leading-6 text-gray-900"
              >
                Nearby Landmark
              </label>
              <div className="mt-2.5">
                <input
                  type="text"
                  name="landmark"
                  placeholder="e.g. Near Park"
                  onChange={handleChange}
                  value={formData.landmark}
                  id="landmark"
                  autoComplete="landmark"
                  className="block w-full rounded-md border-0 px-3.5 py-2 text-gray-900 shadow-sm ring-1 ring-inset ring-secondaryHover
             placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-primary sm:text-sm sm:leading-6"
                />
              </div>
            </div>

            {/* isDefault checkbox */}
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="isDefault"
                checked={formData.isDefault}
                onChange={(e) =>
                  setFormData({ ...formData, isDefault: e.target.checked })
                }
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <label htmlFor="isDefault" className="text-sm text-gray-700">
                Set as default location
              </label>
            </div>
          </div>

          <div className="mt-8 flex justify-end">
            <button
              type="button"
              onClick={handleSubmit}
              className="rounded-md bg-secondary  px-3.5 py-2.5 text-center text-sm font-medium text-textWhite shadow-sm hover:bg-secondaryHover focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
            >
              Save Location
            </button>

            <button
              onClick={onClose}
              className="bg-gray-300 text-black px-4 py-2 rounded"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddressForm;
